import {
  Autocomplete,
  AutocompleteOption,
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  Input,
  List,
  ListDivider,
  ListItem,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/joy";
import { theme } from "../../../../components/Theme";
import { useTranslation } from "react-i18next";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import {
  DISCOUNT_AMOUNT_OPTIONS_ENUM,
  DISCOUNT_FEES_OPTIONS,
  DISCOUNT_FEES_OPTIONS_ENUM,
} from "../../../../types/order";
import { IfElse } from "../../../../components/Condition";
import { NumericFormat } from "react-number-format";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import {
  CustomCreateCatalogType,
  feeValuesFields,
} from "../../../../types/catalog";
import { Fragment, useState } from "react";
import { formatPrice } from "../../../../utils/formatPrice";

type Props = {
  catalog: CustomCreateCatalogType;
};

const DiscountInfoSection = ({ catalog }: Props) => {
  const { t } = useTranslation();

  const {
    control,
    watch,
    formState: { errors },
    setValue,
  } = useFormContext();

  // watched fields
  const currency = watch("currency");
  const feeType = watch("discountFeeConfig.feeType");
  const amountType = watch("discountFeeConfig.amountType");
  const feeValues: feeValuesFields[] = watch("discountFeeConfig.feeValues");

  // discount fee fieldArray
  const { fields, remove, append } = useFieldArray({
    control,
    name: "discountFeeConfig.feeValues",
  });

  const [fieldError, setFieldError] = useState<{
    valueError?: string;
    upToCountError?: string;
  }>();

  const handleFeeValuesValidate = () => {
    let error: {
      valueError?: string;
      upToCountError?: string;
    } = {};

    if (feeValues.length > 1) {
      const curFeeValue = feeValues[feeValues.length - 1];
      const lastFeeValue = feeValues[feeValues.length - 2];

      if (lastFeeValue.value >= curFeeValue.value) {
        let value = `${lastFeeValue.value}`;
        if (amountType !== DISCOUNT_AMOUNT_OPTIONS_ENUM.PERCENTAGE) {
          const price = formatPrice(lastFeeValue.value, currency);
          value = `${currency} ${price}`;
        }
        error.valueError = `${t(
          "catalog.createCatalog.discountFee.discountAmtMustBigger"
        )} ${value}`;
      }

      if (lastFeeValue.upToCount >= curFeeValue.upToCount) {
        const price = formatPrice(lastFeeValue.upToCount, currency);
        const value = `${currency} ${price}`;
        error.upToCountError = `${t(
          "catalog.createCatalog.discountFee.discountAmtMustBigger"
        )} ${value}`;
      }
    }

    if (amountType === DISCOUNT_AMOUNT_OPTIONS_ENUM.PERCENTAGE) {
      const sum = feeValues.reduce((acc, cur) => acc + cur.value, 0);
      if (sum > 100) {
        error.valueError = t(
          "catalog.createCatalog.discountFee.discountPctExceed"
        );
      }
    }

    setFieldError(error);
  };

  return (
    <Box
      sx={{
        borderWidth: "1px",
        borderColor: theme.palette.neutral[300],
        borderRadius: "8px",
        py: "32px",
        px: "24px",
      }}
    >
      <Typography level="text-xl-bold" sx={{ color: "#101828" }}>
        {t("catalog.createCatalog.discountFee.title")}
      </Typography>

      <Controller
        name="discountFeeConfig.feeType"
        control={control}
        render={({ field: { onChange, onBlur, name, value, ref } }) => {
          return (
            <FormControl sx={{ flex: "1", mt: "32px" }} ref={ref}>
              <FormLabel>
                {t("catalog.createCatalog.discountFee.chooseDiscountType")}{" "}
                <span className="text-danger-400">*</span>
              </FormLabel>
              <Autocomplete
                name={name}
                freeSolo={true}
                placeholder={t("catalog.createCatalog.ChooseFeeTypes")}
                options={DISCOUNT_FEES_OPTIONS}
                getOptionLabel={(option) =>
                  typeof option !== "string" ? option?.label : ""
                }
                onChange={(_event, newValue) => {
                  if (typeof newValue !== "string") {
                    onChange(newValue?.value);
                  }
                }}
                value={
                  DISCOUNT_FEES_OPTIONS.find((pft) => pft.value === value) ||
                  null
                }
                onBlur={onBlur}
                sx={{ width: { xs: "100%", lg: "100%" } }}
                renderOption={(props, option) => (
                  <AutocompleteOption
                    {...props}
                    key={option.value}
                    sx={{
                      "&:hover": {
                        backgroundColor: "var(--joy-palette-neutral-100)",
                      },
                    }}
                  >
                    <Stack
                      direction={"row"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                      flex={1}
                    >
                      <Stack>
                        {option.label}
                        <Typography>{option.description}</Typography>
                      </Stack>
                    </Stack>
                  </AutocompleteOption>
                )}
              />
            </FormControl>
          );
        }}
      />
      <FormHelperText>{(errors.feeType as any)?.message}</FormHelperText>

      {feeType !== DISCOUNT_FEES_OPTIONS_ENUM.NONE && (
        <>
          <Divider sx={{ my: "16px" }} />

          <FormControl sx={{ flex: "1", margin: "16px auto" }}>
            <Grid container spacing={2}>
              {/* discount fee type titles */}
              <Grid xs={12} md={6}>
                <Stack direction={"column"} gap={"4px"}>
                  <Typography
                    level={"text-md-semibold"}
                    sx={{ color: theme.palette.common.black }}
                  >
                    {t(`catalog.createCatalog.discountFee.${feeType}`)}
                  </Typography>

                  <Typography
                    level={"text-xs-medium"}
                    sx={{ color: theme.palette.neutral[600] }}
                  >
                    {t(`catalog.createCatalog.discountFee.${feeType}Subtitle`)}
                  </Typography>
                </Stack>
              </Grid>
              {/* discount amount type radio button */}
              <Grid xs={12} md={6}>
                <Stack direction={"column"} sx={{ alignItems: "flex-end" }}>
                  <RadioGroup
                    overlay
                    defaultValue={DISCOUNT_AMOUNT_OPTIONS_ENUM.AMOUNT}
                    onChange={(e) => {
                      setValue("discountFeeConfig.amountType", e.target.value);
                      setValue("discountFeeConfig.feeValues", [
                        { upToCount: 0, value: 0 },
                      ]);
                      setFieldError(undefined);
                    }}
                    value={watch("discountFeeConfig.amountType")}
                  >
                    <List
                      component="div"
                      variant="outlined"
                      orientation={"horizontal"}
                      sx={{
                        borderRadius: "8px",
                        boxShadow: "xs",
                      }}
                    >
                      {Object.values(DISCOUNT_AMOUNT_OPTIONS_ENUM).map(
                        (value, index) => (
                          <Fragment key={value}>
                            {index !== 0 && <ListDivider />}
                            <ListItem>
                              <Radio
                                id={`AmountOption-${value}`}
                                value={value}
                                label={
                                  <Typography
                                    noWrap
                                    level="body-sm"
                                    sx={{
                                      maxWidth: "100%",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {t(
                                      `catalog.createCatalog.discountFee.amountType.${value}`
                                    )}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          </Fragment>
                        )
                      )}
                    </List>
                  </RadioGroup>
                </Stack>
              </Grid>
            </Grid>

            {/* discount fee values */}
            {fields.map((item, index) => (
              <Stack
                key={item.id}
                direction={{ xs: "column", md: "row" }}
                spacing={3}
                sx={{
                  alignItems: "flex-end",
                  py: "12px",
                  // borderWidth: { xs: "1px", md: 0 },
                  // borderRadius: "12px",
                  //   my: { xs: 0, md: "8px" },
                }}
              >
                {/* tier */}
                <Stack
                  sx={{
                    width: { xs: "100%", md: "5%" },
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                  direction={{ xs: "row", md: "column" }}
                >
                  <Stack direction={{ xs: "row", md: "column" }}>
                    <FormLabel>
                      <Typography level="text-sm-semibold">Tier</Typography>
                    </FormLabel>
                    <Typography
                      level="text-sm-semibold"
                      lineHeight={{ md: "40px" }}
                      ml={{ xs: 2 }}
                    >
                      {index + 1}
                    </Typography>
                  </Stack>
                  <IconButton
                    onClick={() => remove(index)}
                    sx={{
                      outline: "1px solid",
                      outlineColor: "#B41F1C",
                      borderRadius: "8px",
                      width: "40px",
                      height: "40px",
                      display: {
                        xs: "inline-flex",
                        md: "none",
                      },
                    }}
                  >
                    <TrashIcon
                      width={24}
                      height={24}
                      color={theme.palette.others.cart_danger_button}
                    />
                  </IconButton>
                </Stack>

                {/* upToCount input field */}
                <Controller
                  name={`discountFeeConfig.feeValues.${index}.upToCount`}
                  defaultValue={
                    catalog?.discountFeeConfig?.feeValues[index]?.upToCount
                  }
                  control={control}
                  render={({ field: { name, onChange, ref, value } }) => (
                    <FormControl className="w-[100%]" ref={ref}>
                      <FormLabel>
                        {t("catalog.createCatalog.discountFee.startFrom")}{" "}
                        <span className="text-danger-400">*</span>
                      </FormLabel>

                      <NumericFormat
                        name={name}
                        customInput={Input}
                        startDecorator={<>{currency}</>}
                        placeholder={t(
                          "catalog.createCatalog.discountFee.startFrom"
                        )}
                        value={value}
                        onValueChange={(v) => {
                          onChange(Number(v.value));
                          handleFeeValuesValidate();
                        }}
                        decimalScale={2}
                        fixedDecimalScale={true}
                        thousandSeparator={currency === "IDR" ? "." : ","}
                        decimalSeparator={currency === "IDR" ? "," : "."}
                      />
                      {index === fields.length - 1 && (
                        <FormHelperText
                          sx={{ display: { xs: "inline-flex", md: "none" } }}
                        >
                          {fieldError?.upToCountError}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />

                {/* value input field */}
                <Controller
                  name={`discountFeeConfig.feeValues.${index}.value`}
                  defaultValue={
                    catalog?.discountFeeConfig?.feeValues[index]?.value
                  }
                  control={control}
                  render={({ field: { name, onChange, ref, value } }) => (
                    <FormControl className="w-[100%]" ref={ref}>
                      <FormLabel>
                        {t("catalog.createCatalog.discountFee.discountAmount")}{" "}
                        <span className="text-danger-400">*</span>
                      </FormLabel>

                      <IfElse
                        condition={
                          amountType !== DISCOUNT_AMOUNT_OPTIONS_ENUM.AMOUNT
                        }
                        ifBlock={
                          <Input
                            name={name}
                            type="number"
                            placeholder={t(
                              "catalog.createCatalog.discountFee.discountAmount"
                            )}
                            endDecorator={
                              <span className="text-black bold">%</span>
                            }
                            aria-label="catalogName"
                            value={value}
                            onChange={(v) => {
                              const inputValue = Number(v.target.value);
                              if (inputValue <= 100) {
                                onChange(inputValue);
                              }
                              handleFeeValuesValidate();
                            }}
                          />
                        }
                        elseBlock={
                          <NumericFormat
                            name={name}
                            customInput={Input}
                            startDecorator={<>{currency}</>}
                            placeholder={t(
                              "catalog.createCatalog.discountFee.discountAmount"
                            )}
                            // disabled={isReadOnly || isAdjustmentOrder}
                            value={value}
                            onValueChange={(v) => {
                              onChange(Number(v.value));
                              // handleCalculatePrice();
                              handleFeeValuesValidate();
                            }}
                            decimalScale={2}
                            fixedDecimalScale={true}
                            // valueIsNumericString={true}
                            thousandSeparator={currency === "IDR" ? "." : ","}
                            decimalSeparator={currency === "IDR" ? "," : "."}
                          />
                        }
                      />
                      {index === fields.length - 1 && (
                        <FormHelperText
                          sx={{ display: { xs: "inline-flex", md: "none" } }}
                        >
                          {fieldError?.valueError}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />

                <IconButton
                  onClick={() => {
                    remove(index);
                    setFieldError(undefined);
                  }}
                  sx={{
                    outline: "1px solid",
                    outlineColor: "#B41F1C",
                    borderRadius: "8px",
                    width: "42px",
                    height: "40px",
                    display: {
                      xs: "none",
                      md: "inline-flex",
                    },
                  }}
                >
                  <TrashIcon
                    width={24}
                    height={24}
                    color={theme.palette.others.cart_danger_button}
                  />
                </IconButton>
              </Stack>
            ))}

            <Grid
              container
              spacing={2}
              sx={{
                position: "relative",
                top: "-12px",
                display: { xs: "none", md: "inline-flex" },
              }}
            >
              <Grid xs={12} md={6}>
                <FormHelperText sx={{ ml: "47px" }}>
                  {fieldError?.upToCountError}
                </FormHelperText>
              </Grid>
              <Grid xs={12} md={6}>
                <FormHelperText>{fieldError?.valueError}</FormHelperText>
              </Grid>
            </Grid>
          </FormControl>

          <Button
            id="catalog-create-discountInfo-addFee-button"
            variant="plain"
            startDecorator={<PlusIcon width={20} height={20} />}
            onClick={() => {
              append({ upToCount: 0, value: 0 });
            }}
            disabled={!(!fieldError?.upToCountError && !fieldError?.valueError)}
          >
            {t("commons.AddFee")}
          </Button>
        </>
      )}
    </Box>
  );
};

export default DiscountInfoSection;
